import { useState, useEffect } from 'react'
import { MerkleTree } from 'merkletreejs'
import { keccak256, encodePacked } from 'viem'
import { Card } from './ui/Card'
import { Button } from './ui/Button'
import { Upload, Download, Copy, Check, Trees } from 'lucide-react'
import toast from 'react-hot-toast'

// Helper function to convert hex string to Uint8Array (browser-compatible)
const hexToUint8Array = (hex: string): Uint8Array => {
  const cleanHex = hex.startsWith('0x') ? hex.slice(2) : hex
  const bytes = new Uint8Array(cleanHex.length / 2)
  for (let i = 0; i < cleanHex.length; i += 2) {
    bytes[i / 2] = parseInt(cleanHex.substr(i, 2), 16)
  }
  return bytes
}

// Helper function to convert Uint8Array to hex string
const uint8ArrayToHex = (bytes: Uint8Array): string => {
  return '0x' + Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

export const MerkleTreeManager = () => {
  const [addresses, setAddresses] = useState<string[]>([])
  const [merkleRoot, setMerkleRoot] = useState<string>('')
  const [merkleTree, setMerkleTree] = useState<MerkleTree | null>(null)
  const [searchAddress, setSearchAddress] = useState('')
  const [proof, setProof] = useState<string[]>([])
  const [copied, setCopied] = useState(false)

  // Load merkle tree data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('merkleTreeData')
    if (savedData) {
      try {
        const data = JSON.parse(savedData)
        if (data.addresses && data.merkleRoot) {
          setAddresses(data.addresses)
          setMerkleRoot(data.merkleRoot)
          // Regenerate tree from saved addresses
          if (data.addresses.length > 0) {
            generateMerkleTreeFromAddresses(data.addresses)
          }
        }
      } catch (error) {
        console.error('Failed to load saved merkle tree:', error)
      }
    }
  }, [])

  // Hash address the same way as Solidity: keccak256(abi.encodePacked(address))
  const hashAddress = (address: string): `0x${string}` => {
    // Normalize address
    const normalized = address.toLowerCase()
    // Use encodePacked to match Solidity's abi.encodePacked(address)
    // encodePacked will create 20 bytes for address, then keccak256 hashes it
    return keccak256(encodePacked(['address'], [normalized as `0x${string}`]))
  }

  // Generate Merkle Tree from addresses
  const generateMerkleTreeFromAddresses = (addressList: string[]) => {
    try {
      // Normalize addresses
      const normalizedAddresses = addressList.map((addr) => addr.toLowerCase())

      // Create leaves - hash address as bytes (same as Solidity: keccak256(abi.encodePacked(address)))
      const leaves = normalizedAddresses.map((addr) => {
        return hashAddress(addr)
      })

      // Create Merkle Tree (using Uint8Array instead of Buffer for browser compatibility)
      const tree = new MerkleTree(
        leaves.map(leaf => hexToUint8Array(leaf)) as any[],
        (data: Uint8Array) => {
          const hex = uint8ArrayToHex(data)
          const hashed = keccak256(hex as `0x${string}`)
          return hexToUint8Array(hashed)
        },
        { sortPairs: true }
      ) as any

      const root = tree.getHexRoot()

      setMerkleTree(tree)
      setMerkleRoot(root)

      // Generate proofs for all addresses
      const proofs: { [key: string]: string[] } = {}
      normalizedAddresses.forEach((addr) => {
        const leaf = hashAddress(addr)
        const leafBytes = hexToUint8Array(leaf)
        proofs[addr] = (tree as any).getHexProof(leafBytes as any)
      })

      // Save to localStorage with proofs
      localStorage.setItem('merkleTreeData', JSON.stringify({
        addresses: normalizedAddresses,
        merkleRoot: root,
        proofs: proofs,
        generatedAt: new Date().toISOString()
      }))

      return { tree, root, proofs }
    } catch (error) {
      console.error('Error generating Merkle tree:', error)
      throw error
    }
  }

  const generateMerkleTree = () => {
    if (addresses.length === 0) {
      toast.error('No addresses provided')
      return
    }

    try {
      const result = generateMerkleTreeFromAddresses(addresses)
      if (result && result.root) {
        toast.success(`Merkle Tree generated! Root: ${result.root.slice(0, 10)}...`)
      } else {
        toast.success('Merkle Tree generated!')
      }
    } catch (error) {
      console.error('Error generating Merkle tree:', error)
      toast.error('Failed to generate Merkle tree')
    }
  }

  // Get proof for specific address
  const getProofForAddress = () => {
    if (!merkleTree || !searchAddress) {
      toast.error('Generate tree first and enter address')
      return
    }

    try {
      const normalizedAddress = searchAddress.toLowerCase()
      // Hash address the same way as Solidity: keccak256(abi.encodePacked(address))
      const leaf = hashAddress(normalizedAddress)
      const leafBytes = hexToUint8Array(leaf)
      const proof = (merkleTree as any).getHexProof(leafBytes as any)

      setProof(proof)
      toast.success(`Proof generated with ${proof.length} elements`)
    } catch (error) {
      console.error('Error getting proof:', error)
      toast.error('Failed to get proof')
    }
  }

  // Load addresses from file
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string
        const parsed = JSON.parse(content)
        
        let addressList: string[] = []
        
        if (Array.isArray(parsed)) {
          addressList = parsed
        } else if (parsed.addresses && Array.isArray(parsed.addresses)) {
          addressList = parsed.addresses
        }

        // Validate addresses
        const validAddresses = addressList.filter((addr) => 
          /^0x[a-fA-F0-9]{40}$/.test(addr)
        )

        if (validAddresses.length === 0) {
          toast.error('No valid addresses found in file')
          return
        }

        setAddresses(validAddresses)
        toast.success(`Loaded ${validAddresses.length} valid addresses`)
      } catch (error) {
        toast.error('Invalid JSON file')
      }
    }
    reader.readAsText(file)
  }

  // Manual address input
  const handleManualInput = (text: string) => {
    const lines = text.split('\n').map(line => line.trim()).filter(Boolean)
    const validAddresses = lines.filter((addr) => /^0x[a-fA-F0-9]{40}$/.test(addr))
    setAddresses(validAddresses)
    toast.success(`Added ${validAddresses.length} valid addresses`)
  }

  // Export data
  const exportData = () => {
    if (!merkleTree || !merkleRoot) {
      toast.error('Generate tree first')
      return
    }

    const proofs: { [key: string]: string[] } = {}
    addresses.forEach((addr) => {
      const normalizedAddress = addr.toLowerCase()
      // Hash address the same way as Solidity: keccak256(abi.encodePacked(address))
      const leaf = hashAddress(normalizedAddress)
      const leafBytes = hexToUint8Array(leaf)
      proofs[normalizedAddress] = (merkleTree as any).getHexProof(leafBytes as any)
    })

    const exportData = {
      merkleRoot,
      totalAddresses: addresses.length,
      generatedAt: new Date().toISOString(),
      addresses: addresses.map(addr => addr.toLowerCase()),
      proofs,
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'merkle-tree-data.json'
    a.click()
    URL.revokeObjectURL(url)

    toast.success('Exported merkle tree data')
  }

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    toast.success('Copied to clipboard')
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Card>
      <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <Trees className="w-5 h-5" />
        Merkle Tree Generator
      </h2>

      <div className="space-y-6">
        {/* Upload Section */}
        <div>
          <h3 className="text-sm font-medium text-slate-300 mb-3">1. Load Addresses</h3>
          
          {/* File Upload */}
          <div className="mb-4">
            <label className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors w-fit">
              <Upload className="w-4 h-4" />
              <span className="text-sm">Upload JSON File</span>
              <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
            <p className="text-xs text-slate-500 mt-2">
              Format: ["0x...", "0x..."] or {`{"addresses": ["0x...", "0x..."]}`}
            </p>
          </div>

          {/* Manual Input */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Or paste addresses (one per line):
            </label>
            <textarea
              rows={5}
              placeholder="0x1234...&#10;0x5678...&#10;0x9abc..."
              onChange={(e) => handleManualInput(e.target.value)}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono text-sm"
            />
          </div>

          {addresses.length > 0 && (
            <div className="mt-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p className="text-green-400 text-sm">
                ✅ {addresses.length} addresses loaded
              </p>
            </div>
          )}
        </div>

        {/* Generate Button */}
        <div>
          <h3 className="text-sm font-medium text-slate-300 mb-3">2. Generate Merkle Tree</h3>
          <Button
            onClick={generateMerkleTree}
            disabled={addresses.length === 0}
            className="w-full"
          >
            Generate Merkle Tree
          </Button>
        </div>

        {/* Merkle Root Display */}
        {merkleRoot && (
          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-3">3. Merkle Root</h3>
            <div className="p-4 bg-slate-800 border border-purple-500/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-400">Root Hash:</span>
                <button
                  onClick={() => copyToClipboard(merkleRoot)}
                  className="text-purple-400 hover:text-purple-300"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-white font-mono text-sm break-all">{merkleRoot}</p>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              ⚠️ Use this root in contract: setMerkleRoot(bytes32)
            </p>
          </div>
        )}

        {/* Get Proof for Address */}
        {merkleTree && (
          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-3">4. Get Proof for Address</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                placeholder="0x..."
                className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              />
              <Button onClick={getProofForAddress} variant="secondary">
                Get Proof
              </Button>
            </div>

            {proof.length > 0 && (
              <div className="mt-4 p-4 bg-slate-800 border border-slate-700 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-400">Proof ({proof.length} elements):</span>
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(proof))}
                    className="text-purple-400 hover:text-purple-300"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <pre className="text-white font-mono text-xs overflow-auto max-h-40">
                  {JSON.stringify(proof, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Export */}
        {merkleTree && (
          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-3">5. Export Data</h3>
            <Button onClick={exportData} variant="secondary" className="w-full">
              <Download className="w-4 h-4 mr-2" />
              Export Merkle Tree Data (JSON)
            </Button>
            <p className="text-xs text-slate-500 mt-2">
              Includes: root, addresses, and proofs for all addresses
            </p>
          </div>
        )}

        {/* Statistics */}
        {merkleTree && (
          <div className="pt-4 border-t border-slate-700">
            <h3 className="text-sm font-medium text-slate-300 mb-3">Statistics</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <p className="text-xs text-slate-400">Addresses</p>
                <p className="text-lg font-bold text-white">{addresses.length}</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <p className="text-xs text-slate-400">Tree Depth</p>
                <p className="text-lg font-bold text-white">{merkleTree.getDepth()}</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <p className="text-xs text-slate-400">Leaf Count</p>
                <p className="text-lg font-bold text-white">{merkleTree.getLeafCount()}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}
